import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useModal } from 'react-modal-hook';
import { Button, IconButton } from '../../../../common/components/buttons';
import { Icon } from '../../../../common/components/icons';
import { PatrolExact } from '../../../models/Patrol';
import useAgent from '../../../../common/hooks/useAgent';
import patrolAgent from '../../../agents/patrolAgent';
import { downloadFile } from '../../../../common/utils/fileMethods';
import useStyles from './HeaderActions.styles';
import usePatrolPdf from '../../../hooks/usePatrolPdf';
import { Loader } from '../../../../common/components/loaders';
import { FindingShape } from '../../../../finding/models/Finding';
import Can, { useAbilityContext } from '../../../../common/contexts/abilityContext';
import Tooltip from '../../../../common/components/Tooltip';
import ConfirmationModal from '../../../../common/components/modals/ConfirmationModal';

const maximumFindingsToGeneratePdf = 500;

const HeaderActions = ({ patrol, findings, onBackClick, filters }) => {
  const classes = useStyles();

  const printPdf = usePatrolPdf();
  const context = useAbilityContext();

  const [downloadReport, downloadedReport, isDownloadingReport] = useAgent(patrolAgent.downloadReport);
  const [publishPatrol, publishedPatrol, isPublishingReport] = useAgent(patrolAgent.publishPatrol);
  const [pushPatrolToIrth, pushedToIrthResponse] = useAgent(patrolAgent.pushToIrth);
  const [reviewPatrol, reviewPatrolResponse] = useAgent(patrolAgent.reviewPatrol);
  const [downloadErrorReport, downloadedErrorReport, isDownloadingErrorReport] = useAgent(patrolAgent.downloadErrorReport);
  const [isPrintingPdf, setIsPrintingPdf] = useState(false);
  const [isDownloadingKml, setIsDownloadingKML] = useState(false);
  const handleDownloadClick = () => {
    setIsDownloadingKML(true);
  };
  useEffect(() => {
    if (isDownloadingKml) {
      setIsDownloadingKML(false);
      downloadReport(patrol.id, filters);
    }
  }, [downloadReport, patrol.id, filters, isDownloadingKml, setIsDownloadingKML]);

  const handleDownloadErrorReportClick = useCallback(() => downloadErrorReport(patrol.id), [downloadErrorReport, patrol.id]);

  const handlePublishClick = useCallback(() => publishPatrol(patrol.id), [publishPatrol, patrol.id]);

  const handlePushToIrthClick = useCallback(() => pushPatrolToIrth(patrol.id), [pushPatrolToIrth, patrol.id]);

  useEffect(() => {
    if (pushedToIrthResponse) {
      toast.success(i18n.t('patrol.message.pushedToIrth'));
    }
  }, [pushedToIrthResponse]);

  const handlePrintToPdfClick = useCallback(async () => {
    setIsPrintingPdf(true);
    const images = await patrolAgent.fetchImages(patrol.id, filters);

    const updatedFindings = findings.map((finding) => {
      const matchingCategory = patrol.clientCategories.find((category) => category.key === finding.label);

      if (matchingCategory) {
        return {
          ...finding,
          label: i18n.language === 'fr' ? matchingCategory.labelFr : matchingCategory.labelEn,
        };
      }
      return finding;
    });

    if (images) printPdf(patrol, updatedFindings, images).then(() => setIsPrintingPdf(false));
    else setIsPrintingPdf(false);
  }, [patrol, filters, printPdf, findings]);

  useEffect(() => {
    if (!isDownloadingReport && downloadedReport && patrol) {
      downloadFile(downloadedReport, patrol.missionName, 'kml', () => null);
    }
  }, [downloadedReport, isDownloadingReport, patrol]);

  useEffect(() => {
    if (!isDownloadingErrorReport && downloadedErrorReport && patrol) {
      downloadFile(downloadedErrorReport, `${patrol.missionName}-error-report`, 'csv', () => null);
    }
  }, [downloadedErrorReport, isDownloadingErrorReport, patrol]);

  useEffect(() => {
    if (publishedPatrol) toast.success(i18n.t('patrol.message.published'));
  }, [publishedPatrol]);

  const isLoading = isPrintingPdf || isDownloadingReport || isPublishingReport || isDownloadingErrorReport;

  const isPdfGenerationDisabled = useMemo(() => (findings || []).length > maximumFindingsToGeneratePdf, [findings]);

  const [showModal, hideModal] = useModal(
    () => (
      <ConfirmationModal closeModal={hideModal} onConfirmClick={() => reviewPatrol(patrol.id)} confirmButtonLabel={i18n.t('common.button.confirm')}>
        {i18n.t('patrol.message.reviewConfirmation')}
      </ConfirmationModal>
    ),
    []
  );

  const handleReviewCompleteClick = useCallback(() => showModal(), [showModal]);
  const [patrolState, setPatrolState] = useState(patrol);

  useEffect(() => {
    if (reviewPatrolResponse && !patrolState.reviewedAt) {
      setPatrolState({
        ...patrolState,
        reviewedAt: new Date(reviewPatrolResponse.reviewedAt),
      });
      toast.success(i18n.t('patrol.message.reviewCompletedSuccessfully'));
    }
  }, [patrolState, reviewPatrolResponse]);

  const reviewedAt = patrolState.reviewedAt || patrol.reviewedAt;
  const reviewCompletedText = reviewedAt ? `${i18n.t('patrol.message.reviewCompleted')}: ${new Date(reviewedAt).toLocaleString()}` : '';

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        {patrol && patrol.id && (
          <>
            {isLoading && <Loader style={{ width: '4rem' }} size={25} />}
            <div className={classes.topRow}>
              <Tooltip
                title={<Typography fontSize={16}>{i18n.t('patrol.button.pdfReportTooltip')}</Typography>}
                placement="top"
                hidden={!isPdfGenerationDisabled}
                classes={classes.pdfTooltip}>
                <IconButton id="downloadPdfButton" onClick={handlePrintToPdfClick} disabled={isPdfGenerationDisabled || isLoading}>
                  <Icon icon="download_for_offline" />
                  {i18n.t('patrol.button.downloadReportPDF')}
                </IconButton>
              </Tooltip>
              <IconButton id="downloadKmlButton" onClick={handleDownloadClick} disabled={isLoading}>
                <Icon icon="download_for_offline" />
                {i18n.t('patrol.button.downloadReportKML')}
              </IconButton>
              <Can I="downloadErrorCsv" a="patrol">
                <IconButton id="downloadErrorReportButton" onClick={handleDownloadErrorReportClick} disabled={isLoading}>
                  <Icon icon="download_for_offline" />
                  {i18n.t('patrol.button.downloadErrorReportCSV')}
                </IconButton>
              </Can>
              <Button id="backButton" onClick={onBackClick} disabled={isLoading}>
                {i18n.t('patrol.button.backToList')}
              </Button>
            </div>
            <div className={classes.bottomRow}>
              <Can I="publish" a="patrol">
                <Button id="publishButton" onClick={handlePublishClick} primary disabled={isLoading}>
                  {i18n.t('patrol.button.publish')}
                </Button>
              </Can>
              {context.can('manage', 'IRTH') && patrol.publishedAt && (
                <Button id="pushToIRTHButton" primary onClick={handlePushToIrthClick} disabled={isLoading}>
                  {i18n.t('patrol.button.pushToIRTH')}
                </Button>
              )}
              <Can I="publish" a="patrol">
                <Tooltip title={reviewCompletedText}>
                  <Button
                    id="reviewCompleteButton"
                    onClick={handleReviewCompleteClick}
                    primary
                    disabled={isLoading || patrol.reviewedAt != null || patrolState.reviewedAt != null}>
                    Review Complete
                  </Button>
                </Tooltip>
              </Can>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

HeaderActions.propTypes = {
  filters: PropTypes.shape().isRequired,
  patrol: PatrolExact.isRequired,
  findings: PropTypes.arrayOf(FindingShape).isRequired,
  onBackClick: PropTypes.func,
};

HeaderActions.defaultProps = {
  onBackClick: undefined,
};

export default HeaderActions;
